"use client";

import { Mutex } from "async-mutex";

import { publicConfig } from "@/configs/public-config";
import { type ITokens } from "@/providers/store/api/auth/auth.interface";
import { setToken } from "@/providers/store/api/auth/functions";
import { type IApiResponse } from "@/shared/interfaces/api-response.interface";


import { TokenService } from "./tokens.service";

export const refreshMutex = new Mutex();

/** Web Lock name shared by all tabs of this origin (tabs share tokens via localStorage). */
export const REFRESH_LOCK_NAME = "enrollcast-token-refresh";

/**
 * - `refreshed`: a valid access token is stored (by this call or by another tab / caller)
 * - `unauthorized`: the refresh token was rejected (401) or is missing; the session is over
 * - `failed`: transient failure (429, 5xx, network); tokens are kept
 */
export type RefreshResult = "refreshed" | "unauthorized" | "failed";

interface IWebLocks {
  request<T>(name: string, callback: () => Promise<T>): Promise<T>;
}

const withCrossTabLock = <T>(callback: () => Promise<T>): Promise<T> => {
  const locks = (globalThis.navigator as { locks?: IWebLocks } | undefined)?.locks;
  if (!locks?.request) {
    return callback();
  }

  return locks.request(REFRESH_LOCK_NAME, callback);
};

const requestNewTokens = async (): Promise<RefreshResult> => {
  const refreshToken = TokenService.getRefreshToken();
  if (!refreshToken) return "unauthorized";

  const url = `${publicConfig.apiUrl}/${publicConfig.apiTag}/auth/refresh-token`;

  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });
  } catch {
    return "failed";
  }

  if (res.status === 401) return "unauthorized";
  if (!res.ok) return "failed";

  try {
    const json = (await res.json()) as IApiResponse<ITokens>;
    const data = json?.data;
    if (!data?.accessToken || !data?.refreshToken) return "failed";

    setToken(data, TokenService.isRememberedSession());
    return "refreshed";
  } catch {
    return "failed";
  }
};

/**
 * Refreshes the access token after a request sent with `failedAccessToken` got a 401.
 * Runs one refresh at a time within the tab (mutex) and across tabs (Web Locks). Tokens are
 * re-read once the locks are held: if another caller already replaced the access token, the
 * refresh is skipped, since the API rotates refresh tokens and a second refresh with the old
 * one would be rejected.
 */
export async function refreshAccessToken(failedAccessToken: string | null): Promise<RefreshResult> {
  return refreshMutex.runExclusive(() => withCrossTabLock(async () => {
    const currentAccessToken = TokenService.getAccessToken();
    if (currentAccessToken && currentAccessToken !== failedAccessToken) {
      return "refreshed";
    }

    return requestNewTokens();
  }));
}
